import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Callout,
  Card,
  LoadingSpinner,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import type { MydataMonthPdfBundlePreview } from '../../types/mydata';
import { MydataPageShell } from './MydataPageShell';
import { MydataYearFilterBar } from './MydataYearFilterBar';

export function MydataPdfExportPage() {
  const labels = t().mydata;
  const monthNames = labels.monthNamesShort;
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [preview, setPreview] = useState<MydataMonthPdfBundlePreview | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const data = await api.getMydataMonthPdfBundlePreview({ year, month });
      setPreview(data);
    } catch (err) {
      setPreview(null);
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  async function handleDownload() {
    setDownloading(true);
    setError(null);
    setMessage(null);
    try {
      const result = await api.downloadMydataMonthPdfBundle({ year, month });
      if (result.failed > 0) {
        setMessage(
          labels.pdfBundleDownloadedPartial
            .replace('{included}', String(result.included))
            .replace('{total}', String(result.included + result.failed)),
        );
      } else {
        setMessage(labels.pdfBundleDownloaded);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <MydataPageShell>
      <div className="shrink-0 space-y-1">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {labels.pdfBundleHint}
        </p>
        <div className="text-xs text-slate-500">
          <Link to="/mydata/documents" className="text-brand hover:underline">
            {labels.documents}
          </Link>
          {' · '}
          <Link to="/mydata/sync" className="text-brand hover:underline">
            {labels.syncPageLink}
          </Link>
        </div>
      </div>

      <MydataYearFilterBar year={year} onYearChange={setYear} />

      <div>
        <div className="mb-1.5 text-xs font-medium text-slate-500">
          {labels.month}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
            const selected = month === m;
            return (
              <button
                key={m}
                type="button"
                onClick={() => setMonth(m)}
                className={[
                  'rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors',
                  selected
                    ? 'border-brand bg-brand-soft text-brand'
                    : 'border-line text-ink-muted hover:border-brand/30',
                ].join(' ')}
              >
                {monthNames[m - 1] ?? m}
              </button>
            );
          })}
        </div>
      </div>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}
      {message ? (
        <Callout variant="success" className="shrink-0">
          {message}
        </Callout>
      ) : null}

      {loading ? (
        <LoadingSpinner />
      ) : preview ? (
        <Card className="shrink-0 space-y-4 p-4">
          <div>
            <h2 className="text-sm font-semibold text-ink">
              {labels.pdfBundleTitle} · {monthNames[month - 1]} {year}
            </h2>
            <p className="mt-1 text-xs text-ink-faint">
              {preview.dateFrom} – {preview.dateTo}
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-line px-3 py-2">
              <div className="text-xs text-ink-faint">
                {labels.pdfBundleTotalDocs}
              </div>
              <div className="mt-1 text-lg font-semibold tabular-nums text-ink">
                {preview.total}
              </div>
            </div>
            <div className="rounded-lg border border-line px-3 py-2">
              <div className="text-xs text-ink-faint">
                {labels.pdfBundleWithPdf}
              </div>
              <div className="mt-1 text-lg font-semibold tabular-nums text-ink">
                {preview.withPdf}
              </div>
            </div>
            <div className="rounded-lg border border-line px-3 py-2">
              <div className="text-xs text-ink-faint">
                {labels.pdfBundleWithoutPdf}
              </div>
              <div className="mt-1 text-lg font-semibold tabular-nums text-ink">
                {preview.withoutPdf}
              </div>
            </div>
          </div>

          <p className="text-xs text-ink-faint">{labels.pdfBundleNote}</p>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              disabled={preview.withPdf === 0 || downloading}
              loading={downloading}
              onClick={() => void handleDownload()}
            >
              {downloading
                ? labels.pdfBundleDownloading
                : labels.pdfBundleDownload}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={loading || downloading}
              onClick={() => void loadPreview()}
            >
              {labels.pdfBundleRefresh}
            </Button>
          </div>
        </Card>
      ) : null}
    </MydataPageShell>
  );
}
